// Browser-side S3 multipart upload: the app starts a session and presigns each
// part, the browser PUTs parts straight to S3, then the app completes the upload.
// The caller owns `state` ({ uploadId, partSize, etags, completed }) and passes the
// same object back to resume: finished parts are skipped, and a completed upload
// returns immediately.
const CONCURRENCY = 4
const PART_ATTEMPTS = 6
// S3 can take minutes to assemble a large upload, so finishing gets ~4-5 minutes.
const FINISH_ATTEMPTS = 12
const BASE_DELAY_MS = 1000
const MAX_DELAY_MS = 30000

export function newUploadState() {
  return { uploadId: null, partSize: null, etags: {}, completed: false }
}

export async function uploadMultipart(file, state, options) {
  const resuming = Boolean(state.uploadId)

  try {
    return await runUpload(file, state, options)
  } catch (error) {
    // The server forgot the session (Redis TTL); start over once with a new one.
    if (!resuming || error.status !== 404) throw error

    Object.assign(state, newUploadState())
    return runUpload(file, state, options)
  }
}

async function runUpload(file, state, { sessionUrl, partsUrl, signal, onStart, onProgress, onFinishing, documentKind }) {
  if (!state.uploadId) {
    const session = await jsonRequest(sessionUrl, "POST", {
      filename: file.name,
      byte_size: file.size,
      document_kind: documentKind
    }, signal)
    Object.assign(state, newUploadState(), { uploadId: session.upload_id, partSize: session.part_size })
    onStart?.(state.uploadId)
  }

  if (state.completed) return state.uploadId

  const slices = slicesOf(file, state.partSize)
  const loaded = slices.map((slice) => (state.etags[slice.partNumber] ? slice.blob.size : 0))
  const report = () => onProgress?.(loaded.reduce((sum, value) => sum + value, 0), file.size)
  const pending = slices.map((slice, index) => ({ slice, index })).filter(({ slice }) => !state.etags[slice.partNumber])
  report()

  const worker = async () => {
    while (pending.length) {
      const { slice, index } = pending.shift()
      state.etags[slice.partNumber] = await putSlice(state.uploadId, slice, partsUrl, signal, (bytes) => {
        loaded[index] = bytes
        report()
      })
    }
  }

  await Promise.all(Array.from({ length: Math.min(CONCURRENCY, pending.length) }, worker))

  onFinishing?.()
  const parts = slices.map((slice) => ({ part_number: slice.partNumber, etag: state.etags[slice.partNumber] }))
  await withRetry(() => jsonRequest(sessionUrl, "PATCH", { upload_id: state.uploadId, parts }, signal), signal, FINISH_ATTEMPTS)
  state.completed = true

  return state.uploadId
}

function slicesOf(file, partSize) {
  const slices = []

  for (let offset = 0, partNumber = 1; offset < file.size; offset += partSize, partNumber += 1) {
    slices.push({
      partNumber,
      blob: file.slice(offset, Math.min(offset + partSize, file.size))
    })
  }

  return slices
}

function putSlice(uploadId, slice, partsUrl, signal, onProgress) {
  return withRetry(async () => {
    const url = await presignPart(partsUrl, uploadId, slice.partNumber, signal)
    return putToS3(url, slice.blob, signal, onProgress)
  }, signal, PART_ATTEMPTS, () => onProgress(0))
}

export async function withRetry(fn, signal, attempts, onRetry) {
  let lastError

  for (let attempt = 1; attempt <= attempts; attempt += 1) {
    try {
      return await fn()
    } catch (error) {
      if (error.name === "AbortError" || !error.retryable) throw error

      lastError = error
      onRetry?.()
      if (attempt < attempts) await sleep(backoffDelay(attempt), signal)
    }
  }

  throw lastError
}

function backoffDelay(attempt) {
  const exponential = Math.min(BASE_DELAY_MS * 2 ** (attempt - 1), MAX_DELAY_MS)
  return exponential / 2 + Math.random() * (exponential / 2)
}

function sleep(ms, signal) {
  return new Promise((resolve, reject) => {
    if (signal?.aborted) {
      reject(new DOMException("Aborted", "AbortError"))
      return
    }

    const timer = setTimeout(resolve, ms)
    signal?.addEventListener("abort", () => {
      clearTimeout(timer)
      reject(new DOMException("Aborted", "AbortError"))
    }, { once: true })
  })
}

async function presignPart(partsUrl, uploadId, partNumber, signal) {
  const base = partsUrl.replace(/upload_parts\/0/, `upload_parts/${partNumber}`)
  const url = `${base}?upload_id=${encodeURIComponent(uploadId)}`
  const data = await jsonRequest(url, "GET", null, signal)
  return data.url
}

// Any S3 failure is worth retrying: a 403 is usually an expired presigned URL,
// and each attempt fetches a fresh one.
function putToS3(url, blob, signal, onProgress) {
  return new Promise((resolve, reject) => {
    const xhr = new XMLHttpRequest()
    xhr.open("PUT", url)
    xhr.upload.onprogress = (event) => {
      if (event.lengthComputable) onProgress(event.loaded)
    }
    xhr.onload = () => {
      if (xhr.status < 200 || xhr.status >= 300) {
        reject(retryableError(`Upload failed (${xhr.status})`))
        return
      }

      const etag = xhr.getResponseHeader("ETag")
      if (!etag) {
        reject(new Error("S3 did not return an ETag. The bucket must expose that header."))
        return
      }

      resolve(etag)
    }
    xhr.onerror = () => reject(retryableError("Upload failed."))
    xhr.onabort = () => reject(new DOMException("Aborted", "AbortError"))
    signal?.addEventListener("abort", () => xhr.abort(), { once: true })
    xhr.send(blob)
  })
}

// 409 means the server is still finalizing; 5xx and 429 are transient.
export async function jsonRequest(url, method, payload, signal) {
  const options = {
    method,
    headers: {
      Accept: "application/json",
      "X-CSRF-Token": csrfToken()
    },
    signal
  }

  if (payload) {
    options.headers["Content-Type"] = "application/json"
    options.body = JSON.stringify(payload)
  }

  let response
  try {
    response = await fetch(url, options)
  } catch (error) {
    if (error.name === "AbortError") throw error
    throw retryableError("The backup could not be uploaded.")
  }

  const data = await response.json().catch(() => ({}))

  if (!response.ok || data.ok === false) {
    const error = new Error(data.error || "The backup could not be uploaded.")
    error.status = response.status
    error.retryable = response.status >= 500 || response.status === 429 || response.status === 409
    throw error
  }

  return data
}

function retryableError(message) {
  const error = new Error(message)
  error.retryable = true
  return error
}

function csrfToken() {
  return document.querySelector("meta[name='csrf-token']")?.content
}
